"use client";

import React, { lazy, Suspense } from "react";
import { motion, useInView } from "framer-motion";
import { Montserrat } from "next/font/google";
import styles from "./GameFeaturesSection.module.css";
import { landingContent } from "@/data/landingContent";
import Image from "next/image";

// Lazy load Rive to reduce initial bundle size and improve LCP
const RiveCrateAnimation = lazy(() => import("./RiveCrateAnimation"));

// Import game benefit images
import crateBg from "@/assets/images/families/gameBenefits/crate2.webp";
import streaksBg from "@/assets/images/families/gameBenefits/streaks.webp";
import questsBg from "@/assets/images/new/why_cove_hits_different/quests_crop.webp";
import decodexBg from "@/assets/images/families/gameBenefits/decodex.webp";
import leaderboardBg from "@/assets/images/new/why_cove_hits_different/leaderboards_crop.webp";
import islandBg from "@/assets/images/new/why_cove_hits_different/IslandEditing.webp";
import { StaticImageData } from "next/image";

const montserrat = Montserrat({ weight: "700", subsets: ["latin"] });

// Map feature icons to background images
const bgImageMap: Record<string, StaticImageData> = {
  crate: crateBg,
  streak: streaksBg,
  quest: questsBg,
  collection: decodexBg,
  leaderboard: leaderboardBg,
  island: islandBg
};

// Map feature icons to benefit emojis
const benefitEmojiMap: Record<string, string> = {
  crate: '🎁',
  streak: '🔥',
  quest: '🎯',
  collection: '📚',
  leaderboard: '🏆',
  island: '🏝️'
};

// Individual card component to handle its own hover/tap state
interface GameFeatureCardProps {
  feature: {
    title: string;
    hook: string;
    benefit: string;
    icon: string;
  };
  index: number;
  isActive: boolean;
  onTap: () => void;
}

function GameFeatureCard({ feature, index, isActive, onTap }: GameFeatureCardProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const bgImage = bgImageMap[feature.icon];
  const benefitEmoji = benefitEmojiMap[feature.icon] || '💰';

  // Show overlay if hovered (desktop) or tapped active (mobile)
  const showOverlay = isHovered || isActive;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      animate={showOverlay ? {
        y: -4,
        boxShadow: '0 20px 40px rgba(122, 207, 63, 0.15), 0 0 0 1px rgba(122, 207, 63, 0.3)',
      } : {
        y: 0,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={onTap}
      className="group rounded-2xl p-6 border border-white/10 cursor-pointer relative overflow-hidden"
      style={{
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        transition: 'border-color 0.3s ease',
      }}
    >
      {/* Background image - fills entire card, anchored at top, with fade out */}
      {bgImage && (
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden"
          style={{
            maskImage: 'linear-gradient(to bottom, black 0%, transparent 70%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 70%)',
          }}
        >
          <Image
            src={bgImage}
            alt=""
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            style={{ objectFit: 'cover', objectPosition: 'top' }}
          />
        </div>
      )}
      {/* Card content - with top padding for image visibility */}
      <div className="relative z-10" style={{ paddingTop: 144 }}>
        <h3
          className="text-xl font-bold text-white mb-2"
          style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)' }}
        >
          {feature.title}
        </h3>
        <p
          className="text-white/70 leading-relaxed"
          style={{ textShadow: '0 2px 6px rgba(0, 0, 0, 0.7)' }}
        >
          {feature.hook}
        </p>
      </div>

      {/* Hover/tap overlay: benefit message */}
      <motion.div
        className="absolute inset-0 z-20 flex items-end rounded-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: showOverlay ? 1 : 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        style={{
          background: 'linear-gradient(to top, rgba(20, 20, 22, 0.95) 0%, rgba(20, 20, 22, 0.9) 40%, transparent 70%)',
          pointerEvents: showOverlay ? 'auto' : 'none',
        }}
      >
        <div className="p-6 w-full">
          <div
            className="rounded-xl px-4 py-3"
            style={{
              background: 'rgba(122, 207, 63, 0.1)',
              border: '1px solid rgba(122, 207, 63, 0.3)',
            }}
          >
            <p className="text-[#7ACF3F] text-sm font-medium leading-relaxed">
              {benefitEmoji} {feature.benefit}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Tap hint for mobile - only show when not active */}
      <motion.div
        className="absolute bottom-2 right-2 z-10 md:hidden"
        initial={{ opacity: 0.6 }}
        animate={{ opacity: showOverlay ? 0 : 0.6 }}
      >
        <span className="text-white/40 text-xs">Tap for more</span>
      </motion.div>
    </motion.div>
  );
}

export default function GameFeaturesSection() {
  const sectionContent = landingContent.gameFeatures;
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  // Track which card is active (tapped) on mobile
  const [activeCardIndex, setActiveCardIndex] = React.useState<number | null>(null);

  const handleCardTap = (index: number) => {
    // Toggle: if already active, deactivate; otherwise activate this card
    setActiveCardIndex(prev => prev === index ? null : index);
  };

  return (
    <section
      ref={sectionRef}
      id="gamefeaturesection"
      className="bg-[#232526] py-24 px-5 xl:px-0 relative"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#7ACF3F] to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00C8F0] to-transparent opacity-50" />
      <div className="absolute top-20 -left-20 w-60 h-60 bg-[#7ACF3F]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 -right-20 w-60 h-60 bg-[#00C8F0]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative overflow-visible">
        {/* Section header */}
        <motion.div
          className="flex flex-col items-center gap-4 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2
            className={`text-4xl md:text-5xl font-bold text-center bg-gradient-to-r from-[#7ACF3F] to-[#00C8F0] inline-block text-transparent bg-clip-text pb-2 ${montserrat.className}`}
          >
            {sectionContent.headline}
          </h2>
          <p className="text-lg md:text-xl text-white/80 text-center max-w-3xl">
            {sectionContent.subheadline}
          </p>
        </motion.div>

        {/* Features grid with crate behind */}
        <div className={styles.featuresWrapper}>
          {/* Crate animation - lazy loaded to improve LCP */}
          <div className={styles.crateContainer}>
            <Suspense fallback={<div className="w-full h-full" />}>
              <RiveCrateAnimation />
            </Suspense>
          </div>

          {/* Features grid - above the crate */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" style={{ position: 'relative', zIndex: 10 }}>
            {sectionContent.features.map((feature, index) => (
              <GameFeatureCard
                key={index}
                feature={feature}
                index={index}
                isActive={activeCardIndex === index}
                onTap={() => handleCardTap(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
